import React from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon, SearchIcon } from '../icons';
import { useCustomizer } from '../../store/customizer';
import {
  Wrap,
  Trigger,
  TriggerMain,
  LabelRow,
  LabelText,
  Req,
  ValueRow,
  ValueName,
  ValueCurrency,
  Placeholder,
  Chevron,
  FlagBox,
  RowFlagImg,
  Menu,
  SearchWrap,
  SearchInput,
  Group,
  GroupName,
  Row,
  RowName,
  SoonPill,
  RowCurrency,
  Empty,
} from './CountryDropdown.styles';

/**
 * Operating-region picker — a two-line trigger (label + selected region: flag ·
 * name · currency) that opens a searchable, grouped menu of countries (flag ·
 * name · "Soon" pill · currency code), with a divider between groups. Selecting a
 * region drives the wizard store (currency switch + map zoom + session save).
 * Mirrors the old Deploy implementation. Used on desktop and the mobile step-1 body.
 */
export const CountryDropdown = (): React.ReactElement => {
  const { countryGroups, country, setCountry } = useCustomizer();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const wrapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const groups = countryGroups
    .map((g) => ({
      ...g,
      countries: q ? g.countries.filter((c) => c.name.toLowerCase().includes(q)) : g.countries,
    }))
    .filter((g) => g.countries.length > 0);

  const onSelect = (code: string): void => {
    setCountry(code);
    setOpen(false);
  };

  return (
    <Wrap ref={wrapRef}>
      <Trigger type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}>
        <TriggerMain>
          <LabelRow>
            <LabelText text="Please select your casino operating area" tag="span" />
            <Req aria-hidden>*</Req>
          </LabelRow>
          <ValueRow>
            {country ? (
              <>
                <FlagBox>{country.flag ? <RowFlagImg src={country.flag} alt="" /> : null}</FlagBox>
                <ValueName text={country.name} tag="span" />
                {country.currency ? <ValueCurrency text={country.currency} tag="span" /> : null}
              </>
            ) : (
              <Placeholder text="Select the operating area" tag="span" />
            )}
          </ValueRow>
        </TriggerMain>
        <Chevron $open={open}>
          <ChevronDownIcon size={20} />
        </Chevron>
      </Trigger>

      {open ? (
        <Menu role="listbox">
          <SearchWrap>
            <SearchIcon size={18} />
            <SearchInput
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('Search country or region')}
              aria-label={t('Search country or region')}
            />
          </SearchWrap>

          {groups.length === 0 ? (
            <Empty text="No countries match “{{query}}”." placeholders={{ query }} tag="paragraph" />
          ) : null}

          {groups.map((g) => (
            <Group key={g.name}>
              <GroupName text={g.name} tag="span" />
              {g.countries.map((c) => (
                <Row
                  key={c.code}
                  type="button"
                  $selected={c.code === country?.code}
                  disabled={!c.isActive}
                  onClick={c.isActive ? () => onSelect(c.code) : undefined}
                >
                  <FlagBox>{c.flag ? <RowFlagImg src={c.flag} alt="" /> : null}</FlagBox>
                  <RowName text={c.name} tag="span" />
                  {c.comingSoon ? <SoonPill text="Soon" tag="span" /> : null}
                  {c.currency ? <RowCurrency text={c.currency} tag="span" /> : null}
                </Row>
              ))}
            </Group>
          ))}
        </Menu>
      ) : null}
    </Wrap>
  );
};

export default CountryDropdown;